import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { UIService } from '@services';
import {
  ExtendedParametersTabsEnum,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsUseCasesStore,
} from '@store';

import type { SeriesColumnOptions, SeriesLineOptions, SeriesPieOptions } from 'highcharts';
import { PillType } from '../../../../../shared/components/pill/pill.model';
import { DiagnosticsChartService } from '../diagnostics-chart.service';

@Component({
  selector: 'app-diagnostics-sticky-chart-panel',
  templateUrl: './diagnostics-sticky-chart-panel.component.html',
  styleUrls: ['./diagnostics-sticky-chart-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsStickyChartPanelComponent implements OnInit, OnDestroy {
  readonly pillType = PillType;

  interval: number;
  gatewaySerial: string;
  isAnyParameterRunningAndSelected$: Observable<boolean>;
  isAllParametersSelected = false;
  allVisibleParameters$: Observable<number>;
  allSelectedParameters$: Observable<number>;
  selectedExtendedParametersTab: ExtendedParametersTabsEnum;
  isAnyParameterRunning$: Observable<boolean>;
  isAnySeriesValid: boolean;
  numberOfSelectedSensorVdds: number;

  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    protected diagnosticsChartService: DiagnosticsChartService,
    protected remoteDiagnosticsStore: RemoteDiagnosticsStore,
    protected uiService: UIService,
    protected remoteDiagnosticsUseCasesStore: RemoteDiagnosticsUseCasesStore,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];
    this.isAnyParameterRunningAndSelected$ = this.remoteDiagnosticsStore.isAnyParameterRunningAndSelected(
      this.gatewaySerial
    );
    this.remoteDiagnosticsUseCasesStore
      .getSelectedExtendedParametersTab(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedParameter) => (this.selectedExtendedParametersTab = selectedParameter));
    this.isAnyParameterRunning$ = this.remoteDiagnosticsStore.isAnyParameterRunning(
      this.gatewaySerial
    );

    this.diagnosticsChartService
      .numberOfSelectedSensorParameters(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((res) => {
        this.numberOfSelectedSensorVdds = res;
      });

    this.allSelectedParameters$ = this.remoteDiagnosticsStore
      .getParametersSelectedOnly(this.gatewaySerial)
      .pipe(map((parameters) => parameters?.length || 0));

    this.allVisibleParameters$ = this.remoteDiagnosticsStore
      .getSelectedParameters(this.gatewaySerial)
      .pipe(map((parameters) => parameters?.length || 0));

    this.diagnosticsChartService.updateFlag.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.setIsAnySeriesValid();
    });

    this.remoteDiagnosticsStore
      .isAnyParameterNotSelected(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isAnyParameterNotSelected) => {
        this.isAllParametersSelected = !isAnyParameterNotSelected;
      });
  }

  allParameterSelectionChange(event: MatCheckboxChange): void {
    this.interval = undefined;
    if (event.checked) {
      this.remoteDiagnosticsStore.selectAllParameters(this.gatewaySerial);
    } else {
      this.remoteDiagnosticsStore.unselectAllParameters(this.gatewaySerial);
    }
  }

  onIntervalChange(event: MatSelectChange): void {
    this.remoteDiagnosticsStore.changeIntervalAllParameters(event.value, this.gatewaySerial);
  }

  startReadingChartParameters(): void {
    this.remoteDiagnosticsStore.scheduleParametersReadCycleAttempt(
      this.gatewaySerial,
      this.interval
    );

    this.uiService.isAnyParameterRunning$.next(true);
  }

  stopReadingParameters(): void {
    this.remoteDiagnosticsStore.cancelMultipleParametersReadCycle(this.gatewaySerial, true);
  }

  changeChartVisibility(): void {
    this.remoteDiagnosticsStore.toggleStickyChartVisibility();
  }

  setIsAnySeriesValid(): void {
    const series = this.diagnosticsChartService.chartOptions.series;
    if (!series?.length) {
      this.isAnySeriesValid = false;

      return;
    }

    this.isAnySeriesValid = series.some((singleSeries) => {
      switch (singleSeries.type) {
        case 'line':
          // now SeriesLineOptions actually exists
          const line = singleSeries as SeriesLineOptions;

          return Array.isArray(line.data) && line.data.length > 0;

        case 'column':
          const col = singleSeries as SeriesColumnOptions;

          return Array.isArray(col.data) && col.data.length > 0;

        case 'pie':
          const pie = singleSeries as SeriesPieOptions;

          return Array.isArray(pie.data) && pie.data.length > 0;

        default:
          return false;
      }
    });
  }

  deleteSeries(): void {
    this.diagnosticsChartService.clearAll();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.remoteDiagnosticsStore.hideStickyChart();
  }
}
